import type { IconNameType } from "@UI/types/icons.ts";
import type { FC, ReactNode } from "react";
import DIcon from "@UI/components/Atoms/Icon/DIcon.tsx";
import classNames from "classnames";

import "./DPlaceholder.scss";

interface Props {
  variant?: "page" | "inline" | "table";
  icon?: IconNameType;
  illustration?: string;
  illustrationAlt?: string;
  illustrationWidth?: number;
  eyebrow?: string;
  title?: string;
  description?: string;
  action?: ReactNode;
  tone?: "primary" | "success";
  className?: string;
}

const DPlaceholder: FC<Props> = ({
  variant = "page",
  icon,
  illustration,
  illustrationAlt,
  illustrationWidth,
  eyebrow,
  title,
  description,
  action,
  tone = "primary",
  className,
}) => {
  const glyph = icon ?? (variant === "table" ? "search" : undefined);
  const iconNode = glyph && (
    <DIcon
      name={glyph}
      color="black"
      width="20px"
      height="20px"
      boxSize="20px"
    />
  );

  if (illustration) {
    return (
      <div
        className={classNames(
          "d-flex-column d-flex-1 d-justify-center d-align-center d-gap-24",
          className,
        )}
      >
        <div className="d-flex-column d-align-center d-gap-16">
          <img
            src={illustration}
            alt={illustrationAlt ?? title ?? ""}
            width={illustrationWidth}
          />
          {(eyebrow || title) && (
            <div className="d-flex-column d-align-center d-gap-8 d-text-center">
              {eyebrow && (
                <span className="d-paragraph-s-semibold d-text-warning">
                  {eyebrow}
                </span>
              )}
              {title && <h2 className="d-h5 d-text-header">{title}</h2>}
            </div>
          )}
          {description && (
            <p className="d-placeholder__description d-paragraph-m-medium d-text-dark d-text-center d-text-pre-line">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
    );
  }

  if (variant !== "page") {
    return (
      <div
        className={classNames(
          "d-placeholder--inline d-flex-column d-flex-1 d-justify-center d-align-center d-gap-4 d-text-center d-text-pre-line",
          className,
        )}
      >
        {iconNode}
        {title && (
          <span className="d-label-m-semibold d-text-header">{title}</span>
        )}
        {description && (
          <span className="d-paragraph-s-regular d-text-light">
            {description}
          </span>
        )}
        {action && <div className="d-placeholder__action">{action}</div>}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "d-flex-column d-flex-1 d-justify-center d-align-center d-gap-12",
        className,
      )}
    >
      {tone === "success" && glyph ? (
        <div className="d-placeholder__tile d-placeholder__tile--success d-flex d-align-center d-justify-center">
          <DIcon name={glyph} color="success" />
        </div>
      ) : (
        iconNode
      )}
      {(title || description) && (
        <div className="d-flex-column d-align-center d-gap-4 d-text-center d-text-pre-line">
          {title && (
            <span className="d-label-m-semibold d-text-header">{title}</span>
          )}
          {description && (
            <span className="d-paragraph-s-regular d-text-light">
              {description}
            </span>
          )}
        </div>
      )}
      {action}
    </div>
  );
};

export default DPlaceholder;
